/**
 * 配信先 1 件の全フィールド編集ダイアログ。
 * 仕様書: docs/01_spec/spec_mail_system.md B-10.3（2026-05-28 追加）
 *
 * 編集対象:
 *  - 氏名（lastName/firstName + フリガナ）/ 性別 / 誕生日 / 職業 / 電話
 *  - 法人情報（companyName / department / jobTitle）
 *  - 個人住所（postalCode / prefecture / city / line）
 *  - 登録メアド（追加・削除・既定指定）
 *  - タグ chip / カスタムフィールド
 *  - 営業メモ（salesMemo）
 */
import type { EnglishAddressFieldKey, MailContactEmail } from '@goenhub/shared';
import {
  COUNTRY_ADDRESS_CONFIGS,
  getCountryAddressConfig,
} from '@goenhub/shared';
import { Timestamp } from 'firebase/firestore';
import { Plus, Save, Star, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import {
  useMailContacts,
  useUpdateMailContact,
  type MailContactRecord,
} from '@/hooks/useMailContacts';


interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: MailContactRecord | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CustomFieldRow {
  key: string;
  value: string;
}

interface EmailRow {
  address: string;
  /** 既存メアドは元の addedAt を保持、新規メアドは Timestamp.now() を入れる */
  addedAt: MailContactEmail['addedAt'];
  /** 既存メアドかどうか（既存は addedAt 維持、新規追加分は新しい Timestamp） */
  isNew: boolean;
}

export function ContactEditDialog({ open, onOpenChange, contact }: Props) {
  const updateMutation = useUpdateMailContact();
  const { data: existingContacts } = useMailContacts({ limit: 5000 });

  // ─── 基本情報 ───
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastNameKana, setLastNameKana] = useState('');
  const [firstNameKana, setFirstNameKana] = useState('');
  const [gender, setGender] = useState('');
  const [birthdate, setBirthdate] = useState(''); // YYYY-MM-DD
  const [occupation, setOccupation] = useState('');
  const [phone, setPhone] = useState('');

  // ─── 法人情報 ───
  const [companyName, setCompanyName] = useState('');
  const [department, setDepartment] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  // ─── 個人住所 ───
  const [postalCode, setPostalCode] = useState('');
  const [prefecture, setPrefecture] = useState('');
  const [city, setCity] = useState('');
  const [addressLine, setAddressLine] = useState('');

  // ─── 英表記登録情報（海外メーカー向け・任意）───
  const [nameEn, setNameEn] = useState('');
  const [phoneIntl, setPhoneIntl] = useState('');
  const [enCountry, setEnCountry] = useState(''); // 国コード（''=未選択）
  const [enAddr, setEnAddr] = useState<Record<EnglishAddressFieldKey, string>>({
    postalCode: '',
    stateRegion: '',
    city: '',
    line1: '',
    line2: '',
  });
  const enConfig = getCountryAddressConfig(enCountry);
  const setEnAddrField = (key: EnglishAddressFieldKey, value: string) =>
    setEnAddr((prev) => ({ ...prev, [key]: value }));

  // ─── メアド管理 ───
  const [emailRows, setEmailRows] = useState<EmailRow[]>([]);
  const [defaultEmail, setDefaultEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');

  // ─── タグ ───
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // ─── カスタムフィールド ───
  const [customFields, setCustomFields] = useState<CustomFieldRow[]>([]);

  // ─── 営業メモ ───
  const [salesMemo, setSalesMemo] = useState('');

  // ─── テスト顧客フラグ (動作確認用。商談の自動マージ対象外にする) ───
  const [isTestContact, setIsTestContact] = useState(false);

  // タグサジェスト用
  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const c of existingContacts ?? []) {
      for (const t of c.tags ?? []) if (t.trim()) set.add(t);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ja'));
  }, [existingContacts]);

  const filteredTags = useMemo(() => {
    const k = tagInput.trim().toLowerCase();
    if (!k) return allTags;
    return allTags.filter((t) => t.toLowerCase().includes(k));
  }, [allTags, tagInput]);

  // open & contact 変化時に状態を初期化
  useEffect(() => {
    if (!open || !contact) return;
    setLastName(contact.lastName ?? '');
    setFirstName(contact.firstName ?? '');
    setLastNameKana(contact.lastNameKana ?? '');
    setFirstNameKana(contact.firstNameKana ?? '');
    setGender(contact.gender ?? '');
    setBirthdate(contact.birthdate ?? '');
    setOccupation(contact.occupation ?? '');
    setPhone(contact.phone ?? '');

    setCompanyName(contact.companyName ?? '');
    setDepartment(contact.department ?? '');
    setJobTitle(contact.jobTitle ?? '');

    setPostalCode(contact.personalAddress?.postalCode ?? '');
    setPrefecture(contact.personalAddress?.prefecture ?? '');
    setCity(contact.personalAddress?.city ?? '');
    setAddressLine(contact.personalAddress?.line ?? '');

    setNameEn(contact.nameEn ?? '');
    setPhoneIntl(contact.phoneIntl ?? '');
    setEnCountry(contact.englishAddress?.country ?? '');
    setEnAddr({
      postalCode: contact.englishAddress?.postalCode ?? '',
      stateRegion: contact.englishAddress?.stateRegion ?? '',
      city: contact.englishAddress?.city ?? '',
      line1: contact.englishAddress?.line1 ?? '',
      line2: contact.englishAddress?.line2 ?? '',
    });

    const initEmails: EmailRow[] = (contact.emails ?? []).map((e) => ({
      address: e.address,
      addedAt: e.addedAt,
      isNew: false,
    }));
    setEmailRows(initEmails);
    setDefaultEmail(contact.defaultEmail ?? '');
    setNewEmail('');

    setTags(contact.tags ?? []);
    setTagInput('');

    const cfRows: CustomFieldRow[] = Object.entries(contact.customFields ?? {}).map(
      ([k, v]) => ({ key: k, value: String(v ?? '') }),
    );
    setCustomFields(cfRows);

    setSalesMemo(contact.salesMemo ?? '');
    setIsTestContact(contact.isTestContact === true);
  }, [open, contact]);

  // ─── タグ操作 ───
  const addTag = (raw: string) => {
    const parts = raw
      .split(/[,、\t\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    setTags((prev) => {
      const next = [...prev];
      for (const p of parts) if (!next.includes(p)) next.push(p);
      return next;
    });
    setTagInput('');
  };
  const toggleTag = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };
  const removeTag = (t: string) => {
    setTags((prev) => prev.filter((x) => x !== t));
  };

  // ─── カスタムフィールド操作 ───
  const addCustomField = () => {
    setCustomFields((prev) => [...prev, { key: '', value: '' }]);
  };
  const updateCustomField = (idx: number, patch: Partial<CustomFieldRow>) => {
    setCustomFields((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };
  const removeCustomField = (idx: number) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== idx));
  };

  // ─── メアド操作 ───
  const addEmailRow = () => {
    const addr = newEmail.trim().toLowerCase();
    if (!addr) return;
    if (!EMAIL_RE.test(addr)) {
      toast.error('メアドの形式が不正です');
      return;
    }
    if (emailRows.some((r) => r.address === addr)) {
      toast.error('既に登録されているメアドです');
      return;
    }
    setEmailRows((prev) => [
      ...prev,
      { address: addr, addedAt: Timestamp.now(), isNew: true },
    ]);
    if (!defaultEmail) setDefaultEmail(addr);
    setNewEmail('');
  };
  const removeEmailRow = (addr: string) => {
    setEmailRows((prev) => {
      const next = prev.filter((r) => r.address !== addr);
      // 既定が消えたら先頭を既定に
      if (addr === defaultEmail) {
        setDefaultEmail(next[0]?.address ?? '');
      }
      return next;
    });
  };

  // ─── 保存 ───
  const handleSubmit = async () => {
    if (!contact) return;

    // 確定し忘れたタグも自動でタグ化
    const trailingTag = tagInput.trim();
    const finalTags =
      trailingTag && !tags.includes(trailingTag) ? [...tags, trailingTag] : tags;

    // 確定し忘れた新規メアド入力は無視（明示的 add ボタンのみ反映）

    if (emailRows.length === 0) {
      toast.error('メアドを最低 1 件登録してください');
      return;
    }
    if (!defaultEmail || !emailRows.some((r) => r.address === defaultEmail)) {
      toast.error('既定配信先を選択してください');
      return;
    }

    // 表示名は lastName/firstName から再構築
    const composedName = [lastName.trim(), firstName.trim()]
      .filter((x) => x)
      .join(' ');
    const finalName = composedName || contact.name || '';

    // customFields を Record にまとめる
    const cfObj: Record<string, string> = {};
    for (const row of customFields) {
      const k = row.key.trim();
      const v = row.value.trim();
      if (k && v) cfObj[k] = v;
    }

    // personalAddress（全部空なら null）
    const pa = {
      postalCode: postalCode.trim() || null,
      prefecture: prefecture.trim() || null,
      city: city.trim() || null,
      line: addressLine.trim() || null,
    };
    const hasPa = !!(pa.postalCode || pa.prefecture || pa.city || pa.line);

    // 英表記住所（国 or いずれかのフィールドがあれば保存、全空なら null）
    const enAddrObj = {
      country: enCountry.trim() || null,
      postalCode: enAddr.postalCode.trim() || null,
      stateRegion: enAddr.stateRegion.trim() || null,
      city: enAddr.city.trim() || null,
      line1: enAddr.line1.trim() || null,
      line2: enAddr.line2.trim() || null,
    };
    const hasEnAddr = !!(
      enAddrObj.country ||
      enAddrObj.postalCode ||
      enAddrObj.stateRegion ||
      enAddrObj.city ||
      enAddrObj.line1 ||
      enAddrObj.line2
    );

    try {
      await updateMutation.mutateAsync({
        id: contact.id,
        data: {
          name: finalName,
          lastName: lastName.trim() || null,
          firstName: firstName.trim() || null,
          lastNameKana: lastNameKana.trim() || null,
          firstNameKana: firstNameKana.trim() || null,
          gender: gender.trim() || null,
          birthdate: birthdate.trim() || null,
          occupation: occupation.trim() || null,
          phone: phone.trim() || null,
          companyName: companyName.trim() || null,
          department: department.trim() || null,
          jobTitle: jobTitle.trim() || null,
          personalAddress: hasPa ? pa : null,
          nameEn: nameEn.trim() || null,
          phoneIntl: phoneIntl.trim() || null,
          englishAddress: hasEnAddr ? enAddrObj : null,
          emails: emailRows.map((r) => ({
            address: r.address,
            addedAt: r.addedAt,
          })),
          defaultEmail,
          tags: finalTags,
          customFields: cfObj,
          salesMemo: salesMemo.trim() || null,
          isTestContact,
        },
      });
      toast.success(`「${finalName || defaultEmail}」を更新しました`);
      onOpenChange(false);
    } catch (err) {
      toast.error(`更新失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>配信先を編集</DialogTitle>
          <DialogDescription>
            {contact.name || contact.defaultEmail} の情報を編集します。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* 基本情報 */}
          <section className="space-y-3">
            <h3 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
              基本情報
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">姓</Label>
                <Input
                  type="text"
                  placeholder="山田"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">名</Label>
                <Input
                  type="text"
                  placeholder="太郎"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">姓（カナ）</Label>
                <Input
                  type="text"
                  placeholder="ヤマダ"
                  value={lastNameKana}
                  onChange={(e) => setLastNameKana(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">名（カナ）</Label>
                <Input
                  type="text"
                  placeholder="タロウ"
                  value={firstNameKana}
                  onChange={(e) => setFirstNameKana(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">性別</Label>
                <Input
                  type="text"
                  placeholder="男 / 女 / その他"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">誕生日</Label>
                <Input
                  type="date"
                  value={birthdate}
                  onChange={(e) => setBirthdate(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">職業</Label>
                <Input
                  type="text"
                  placeholder="例: 自営業 / 会社員"
                  value={occupation}
                  onChange={(e) => setOccupation(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">電話番号</Label>
                <Input
                  type="tel"
                  placeholder="09012345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* 法人情報 */}
          <section className="space-y-3">
            <h3 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
              法人情報（BtoB 用・任意）
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">会社名</Label>
                <Input
                  type="text"
                  placeholder="株式会社 ◯◯"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">部署</Label>
                <Input
                  type="text"
                  placeholder="営業部"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">役職</Label>
                <Input
                  type="text"
                  placeholder="部長"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* 住所 */}
          <section className="space-y-3">
            <h3 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
              個人住所（任意）
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">郵便番号</Label>
                <Input
                  type="text"
                  placeholder="1500001"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">都道府県</Label>
                <Input
                  type="text"
                  placeholder="東京都"
                  value={prefecture}
                  onChange={(e) => setPrefecture(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">市区町村</Label>
                <Input
                  type="text"
                  placeholder="渋谷区"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">町名・番地・建物</Label>
                <Input
                  type="text"
                  placeholder="神宮前 5-50-10 びゅあ 1 ビル 1F"
                  value={addressLine}
                  onChange={(e) => setAddressLine(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* 英表記登録情報 */}
          <section className="space-y-3">
            <h3 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
              英表記登録情報（海外メーカー向け・任意）
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">英語名（Name）</Label>
                <Input
                  type="text"
                  placeholder="Yuki Teraoka"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">電話番号（国番号付き）</Label>
                <Input
                  type="tel"
                  placeholder="+81 90-1234-5678"
                  value={phoneIntl}
                  onChange={(e) => setPhoneIntl(e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">国（Country）</Label>
                <select
                  value={enCountry}
                  onChange={(e) => setEnCountry(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-sm"
                >
                  <option value="">選択してください…</option>
                  {COUNTRY_ADDRESS_CONFIGS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.nameJa}（{c.name}）
                    </option>
                  ))}
                </select>
              </div>
              {/* 国別の住所フィールド（country 選択時のみ） */}
              {enCountry &&
                enConfig.fields.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs">
                      {f.labelJa}（{f.label}）
                    </Label>
                    <Input
                      type="text"
                      placeholder={f.placeholder}
                      value={enAddr[f.key]}
                      onChange={(e) => setEnAddrField(f.key, e.target.value)}
                    />
                  </div>
                ))}
            </div>
            {!enCountry && (
              <p className="text-[11px] text-[var(--text-muted)] italic">
                国を選択すると、その国に合わせた住所入力欄が表示されます。
              </p>
            )}
          </section>

          {/* メアド管理 */}
          <section className="space-y-2">
            <h3 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
              登録メアド（★ = 既定配信先）
            </h3>
            <div className="space-y-1">
              {emailRows.map((r) => (
                <div
                  key={r.address}
                  className="flex items-center gap-2 px-2 py-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)]"
                >
                  <button
                    type="button"
                    onClick={() => setDefaultEmail(r.address)}
                    title="既定配信先にする"
                  >
                    <Star
                      className={
                        r.address === defaultEmail
                          ? 'size-4 fill-[var(--accent)] text-[var(--accent)]'
                          : 'size-4 text-[var(--text-muted)] hover:text-[var(--accent)]'
                      }
                    />
                  </button>
                  <span className="font-mono text-xs flex-1 truncate">{r.address}</span>
                  {r.isNew && (
                    <span className="text-[10px] text-[var(--accent)] font-semibold">
                      NEW
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeEmailRow(r.address)}
                    disabled={emailRows.length <= 1}
                    title={
                      emailRows.length <= 1 ? '最低 1 件は必要です' : 'このメアドを削除'
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="email"
                placeholder="新規メアドを追加（Enter で確定）"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => {
                  const composing = e.nativeEvent.isComposing || e.keyCode === 229;
                  if (e.key === 'Enter' && !composing) {
                    e.preventDefault();
                    addEmailRow();
                  }
                }}
                className="flex-1"
              />
              <Button type="button" variant="secondary" size="sm" onClick={addEmailRow}>
                <Plus className="size-3 mr-1" />
                追加
              </Button>
            </div>
          </section>

          {/* タグ chip */}
          <section>
            <Label className="text-xs">タグ（任意・複数可）</Label>
            <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-sm bg-[var(--bg-input)] border border-[var(--border)] focus-within:border-[var(--border-focus)] transition-colors min-h-[2.25rem]">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    className="hover:text-[var(--accent-red)] -mr-0.5"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                placeholder={
                  tags.length === 0 ? 'タグを入力（Enter で確定）' : '追加タグ'
                }
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  const composing = e.nativeEvent.isComposing || e.keyCode === 229;
                  if (e.key === 'Enter' && !composing) {
                    e.preventDefault();
                    addTag(tagInput);
                  } else if (
                    e.key === 'Backspace' &&
                    tagInput === '' &&
                    tags.length > 0 &&
                    !composing
                  ) {
                    setTags((p) => p.slice(0, -1));
                  }
                }}
                onBlur={() => {
                  if (tagInput.trim()) addTag(tagInput);
                }}
                className="flex-1 min-w-[12ch] bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
              />
            </div>
            {allTags.length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] text-[var(--text-muted)] mb-1">
                  既存タグから選択
                </div>
                <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                  {filteredTags.map((t) => {
                    const sel = tags.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleTag(t)}
                        className={
                          'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border transition-colors ' +
                          (sel
                            ? 'bg-[var(--accent-subtle)] text-[var(--accent)] border-[var(--accent)]'
                            : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] border-transparent hover:border-[var(--accent)] hover:text-[var(--accent)]')
                        }
                      >
                        {sel && <span className="mr-1">✓</span>}
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* カスタムフィールド */}
          <section>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs !mb-0">カスタムフィールド（任意）</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addCustomField}>
                <Plus className="size-3 mr-1" />
                フィールド追加
              </Button>
            </div>
            {customFields.length === 0 ? (
              <p className="text-[11px] text-[var(--text-muted)] italic">
                追加するフィールドがあれば「+ フィールド追加」を押してください
              </p>
            ) : (
              <div className="space-y-1">
                {customFields.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="キー名"
                      value={row.key}
                      onChange={(e) => updateCustomField(idx, { key: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="text"
                      placeholder="値"
                      value={row.value}
                      onChange={(e) =>
                        updateCustomField(idx, { value: e.target.value })
                      }
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCustomField(idx)}
                      title="このフィールドを削除"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* 営業メモ */}
          <section>
            <Label className="text-xs">営業メモ（任意）</Label>
            <Textarea
              rows={3}
              placeholder="例: 決裁者は社長、技術担当は田中さん。導入は会計年度末を希望"
              value={salesMemo}
              onChange={(e) => setSalesMemo(e.target.value)}
            />
          </section>

          {/* テスト顧客フラグ (動作確認用) */}
          <section className="rounded-md border border-[var(--accent-red-subtle)] px-3">
            <Switch
              label="テスト顧客 (動作確認用)"
              description="ON にすると、このコンタクトのフォーム送信は既存商談に自動マージされず毎回新しい商談が作られます。保留トレイにも積まれません。本番のお客様には付けないでください。"
              checked={isTestContact}
              onChange={(e) => setIsTestContact(e.target.checked)}
            />
          </section>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <Save className="size-4 mr-1" />
            )}
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
